import { create } from 'zustand';
import type { RealtimeEvent } from '@/api/types';
import type { ConnectionState } from '@/websocket/client';

const MAX_EVENTS = 200;

interface RealtimeState {
  connection: ConnectionState;
  events: RealtimeEvent[];
  /**
   * A sequence gap was seen and the refetch it triggers has not finished.
   * Not set by an ordinary (re)connect: every connection refetches anyway, and
   * treating that as "events were missed" put a warning on the Overview page
   * after every page load and every session renewal, where it stayed.
   */
  resyncNeeded: boolean;
  lastSeqByChannel: Record<string, number>;
  setConnection: (state: ConnectionState) => void;
  ingest: (event: RealtimeEvent) => void;
  /** A new connection: state is refetched, so sequence tracking starts over. */
  connected: () => void;
  requireResync: () => void;
  resyncHandled: () => void;
  reset: () => void;
}

export const useRealtime = create<RealtimeState>((set) => ({
  connection: 'idle',
  events: [],
  resyncNeeded: false,
  lastSeqByChannel: {},

  setConnection: (connection) => set({ connection }),

  /**
   * Record an event and watch for gaps.
   *
   * A missing sequence number means at least one event was lost — coalesced
   * away, dropped under backpressure, or missed across a reconnect. The socket
   * is a notification channel, so the correct response is to refetch state over
   * REST rather than to patch the local view from an incomplete stream.
   */
  ingest: (event) =>
    set((state) => {
      const previous = state.lastSeqByChannel[event.channel];
      const gap = previous !== undefined && event.seq > previous + 1;
      return {
        events: [event, ...state.events].slice(0, MAX_EVENTS),
        // Never moves backwards: several processes publish to one channel, so
        // an earlier number can arrive just after a later one. That late
        // event is not a gap, and must not make the next one look like one.
        lastSeqByChannel: {
          ...state.lastSeqByChannel,
          [event.channel]: previous === undefined ? event.seq : Math.max(previous, event.seq),
        },
        resyncNeeded: state.resyncNeeded || gap,
      };
    }),

  connected: () => set({ lastSeqByChannel: {} }),
  requireResync: () => set({ resyncNeeded: true }),
  resyncHandled: () => set({ resyncNeeded: false }),
  reset: () => set({ events: [], lastSeqByChannel: {}, resyncNeeded: false }),
}));
